import api, { apiHelpers } from './api';
import type {
  ApiResponse,
  PaginatedResponse,
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  ChangePasswordRequest,
  RefreshTokenResponse,
  User,
  Student,
  Teacher,
  Parent,
  Class,
  Subject,
  Grade,
  Attendance,
  Schedule,
  Event,
  Payment,
  DashboardStats,
  Report,
  Notification,
  FileUpload,
  SchoolSettings,
  BulkOperation,
  PaginationParams,
  FilterParams,  
} from '@/types/api';

// Helper function to build query string
function buildQueryString(params: Record<string, any>): string {
  const searchParams = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      if (Array.isArray(value)) {
        value.forEach(item => searchParams.append(key, item.toString()));
      } else {
        searchParams.append(key, value.toString());
      }
    }
  });
  
  return searchParams.toString();
}

// Authentication endpoints
export const authApi = {
  login: async (data: LoginRequest): Promise<LoginResponse> => {
    const response = await api.post('/auth/login', data);
    return response.data;
  },
  
  register: async (data: RegisterRequest): Promise<RegisterResponse> => {
    const response = await api.post('/auth/register', data);
    return response.data;
  },
  
  logout: async (): Promise<void> => {
    await api.post('/auth/logout');
  },
  
  refreshToken: async (refreshToken: string): Promise<RefreshTokenResponse> => {
    const response = await api.post('/auth/refresh', { refreshToken });
    return response.data;
  },
  
  forgotPassword: async (data: ForgotPasswordRequest): Promise<void> => {
    await api.post('/auth/forgot-password', data);
  },
  
  resetPassword: async (data: ResetPasswordRequest): Promise<void> => {
    await api.post('/auth/reset-password', data);
  },
  
  changePassword: async (data: ChangePasswordRequest): Promise<void> => {
    await api.post('/auth/change-password', data);
  },
  
  me: async (): Promise<User> => {
    const response = await api.get('/auth/me');
    return response.data;
  },
  
  updateProfile: async (data: Partial<User>): Promise<User> => {
    const response = await api.patch('/auth/profile', data);
    return response.data;
  },
  
  verifyEmail: async (token: string): Promise<void> => {
    await api.post('/auth/verify-email', { token });
  },
  
  resendVerification: async (): Promise<void> => {
    await api.post('/auth/resend-verification');
  },
};

// Users endpoints
export const usersApi = {
  list: async (params?: PaginationParams & FilterParams): Promise<PaginatedResponse<User>> => {
    const queryString = params ? buildQueryString(params) : '';
    const response = await api.get(`/users${queryString ? `?${queryString}` : ''}`);
    return response.data;
  },
  
  get: async (id: string): Promise<User> => {
    const response = await api.get(`/users/${id}`);
    return response.data;
  },
  
  create: async (data: Partial<User>): Promise<User> => {
    const response = await api.post('/users', data);
    return response.data;
  },
  
  update: async (id: string, data: Partial<User>): Promise<User> => {
    const response = await api.patch(`/users/${id}`, data);
    return response.data;
  },
  
  delete: async (id: string): Promise<void> => {
    await api.delete(`/users/${id}`);
  },
  
  bulkCreate: async (data: BulkOperation<Partial<User>>): Promise<ApiResponse<User[]>> => {
    const response = await api.post('/users/bulk', data);
    return response.data;
  },
  
  bulkUpdate: async (data: BulkOperation<Partial<User>>): Promise<ApiResponse<User[]>> => {
    const response = await api.patch('/users/bulk', data);
    return response.data;
  },
  
  bulkDelete: async (ids: string[]): Promise<void> => {
    await api.delete('/users/bulk', { data: { ids } });
  },
  
  export: async (params?: FilterParams): Promise<void> => {
    const queryString = params ? buildQueryString(params) : '';
    await apiHelpers.downloadFile(`/users/export${queryString ? `?${queryString}` : ''}`, 'users.xlsx');
  },
  
  import: async (file: File, onProgress?: (progress: number) => void): Promise<ApiResponse<any>> => {
    const response = await apiHelpers.uploadFile('/users/import', file, onProgress);
    return response.data;
  },
};

// Students endpoints
export const studentsApi = {
  list: async (params?: PaginationParams & FilterParams): Promise<PaginatedResponse<Student>> => {
    const queryString = params ? buildQueryString(params) : '';
    const response = await api.get(`/students${queryString ? `?${queryString}` : ''}`);
    return response.data;
  },
  
  get: async (id: string): Promise<Student> => {
    const response = await api.get(`/students/${id}`);
    return response.data;
  },
  
  create: async (data: Partial<Student>): Promise<Student> => {
    const response = await api.post('/students', data);
    return response.data;
  },
  
  update: async (id: string, data: Partial<Student>): Promise<Student> => {
    const response = await api.patch(`/students/${id}`, data);
    return response.data;
  },
  
  delete: async (id: string): Promise<void> => {
    await api.delete(`/students/${id}`);
  },
  
  getGrades: async (id: string, params?: PaginationParams & FilterParams): Promise<PaginatedResponse<Grade>> => {
    const queryString = params ? buildQueryString(params) : '';
    const response = await api.get(`/students/${id}/grades${queryString ? `?${queryString}` : ''}`);
    return response.data;
  },
  
  getAttendance: async (id: string, params?: PaginationParams & FilterParams): Promise<PaginatedResponse<Attendance>> => {
    const queryString = params ? buildQueryString(params) : '';
    const response = await api.get(`/students/${id}/attendance${queryString ? `?${queryString}` : ''}`);
    return response.data;
  },
  
  getSchedule: async (id: string): Promise<Schedule[]> => {
    const response = await api.get(`/students/${id}/schedule`);
    return response.data;
  },
  
  getReportCard: async (id: string, period?: string): Promise<Report> => {
    const queryString = period ? buildQueryString({ period }) : '';
    const response = await api.get(`/students/${id}/report-card${queryString ? `?${queryString}` : ''}`);
    return response.data;
  },
  
  getFinancial: async (id: string, params?: PaginationParams & FilterParams): Promise<PaginatedResponse<Payment>> => {
    const queryString = params ? buildQueryString(params) : '';
    const response = await api.get(`/students/${id}/financial${queryString ? `?${queryString}` : ''}`);
    return response.data;
  },
  
  export: async (params?: FilterParams): Promise<void> => {
    const queryString = params ? buildQueryString(params) : '';
    await apiHelpers.downloadFile(`/students/export${queryString ? `?${queryString}` : ''}`, 'students.xlsx');
  },
  
  import: async (file: File, onProgress?: (progress: number) => void): Promise<ApiResponse<any>> => {
    const response = await apiHelpers.uploadFile('/students/import', file, onProgress);
    return response.data;
  },
  getByClass: async (classId: string): Promise<ApiResponse<Student[]>> => {
    const response = await api.get(`/students/by-class/${classId}`);
    return response.data;
  },
  getAll: async (): Promise<ApiResponse<Student[]>> => {
    const response = await api.get('/students/all');
    return response.data;
  },
};

// Teachers endpoints
export const teachersApi = {
  list: async (params?: PaginationParams & FilterParams): Promise<PaginatedResponse<Teacher>> => {
    const queryString = params ? buildQueryString(params) : '';
    const response = await api.get(`/teachers${queryString ? `?${queryString}` : ''}`);
    return response.data;
  },
  
  get: async (id: string): Promise<Teacher> => {
    const response = await api.get(`/teachers/${id}`);
    return response.data;
  },
  
  create: async (data: Partial<Teacher>): Promise<Teacher> => {
    const response = await api.post('/teachers', data);
    return response.data;
  },
  
  update: async (id: string, data: Partial<Teacher>): Promise<Teacher> => {
    const response = await api.patch(`/teachers/${id}`, data);
    return response.data;
  },
  
  delete: async (id: string): Promise<void> => {
    await api.delete(`/teachers/${id}`);
  },
  
  getClasses: async (id: string): Promise<Class[]> => {
    const response = await api.get(`/teachers/${id}/classes`);
    return response.data;
  },
  
  getSchedule: async (id: string): Promise<Schedule[]> => {
    const response = await api.get(`/teachers/${id}/schedule`);
    return response.data;
  },
  
  getSubjects: async (id: string): Promise<Subject[]> => {
    const response = await api.get(`/teachers/${id}/subjects`);
    return response.data;
  },
  
  getStudents: async (id: string): Promise<Student[]> => {
    const response = await api.get(`/teachers/${id}/students`);
    return response.data;
  },
  
  export: async (params?: FilterParams): Promise<void> => {
    const queryString = params ? buildQueryString(params) : '';
    await apiHelpers.downloadFile(`/teachers/export${queryString ? `?${queryString}` : ''}`, 'teachers.xlsx');
  },
};

// Parents endpoints
export const parentsApi = {
  list: async (params?: PaginationParams & FilterParams): Promise<PaginatedResponse<Parent>> => {
    const queryString = params ? buildQueryString(params) : '';
    const response = await api.get(`/parents${queryString ? `?${queryString}` : ''}`);
    return response.data;
  },
  
  get: async (id: string): Promise<Parent> => {
    const response = await api.get(`/parents/${id}`);
    return response.data;
  },
  
  create: async (data: Partial<Parent>): Promise<Parent> => {
    const response = await api.post('/parents', data);
    return response.data;
  },
  
  update: async (id: string, data: Partial<Parent>): Promise<Parent> => {
    const response = await api.patch(`/parents/${id}`, data);
    return response.data;
  },
  
  delete: async (id: string): Promise<void> => {
    await api.delete(`/parents/${id}`);
  },
  
  getChildren: async (id: string): Promise<Student[]> => {
    const response = await api.get(`/parents/${id}/children`);
    return response.data;
  },
  
  linkChild: async (parentId: string, studentId: string): Promise<void> => {
    await api.post(`/parents/${parentId}/children`, { studentId });
  },
  
  unlinkChild: async (parentId: string, studentId: string): Promise<void> => {
    await api.delete(`/parents/${parentId}/children/${studentId}`);
  },
};

// Classes endpoints
export const classesApi = {
  list: async (params?: PaginationParams & FilterParams): Promise<PaginatedResponse<Class>> => {
    const queryString = params ? buildQueryString(params) : '';
    const response = await api.get(`/classes${queryString ? `?${queryString}` : ''}`);
    return response.data;
  },
  
  getAll: async (): Promise<ApiResponse<Class[]>> => {
    const response = await api.get('/classes/all');
    return response.data;
  },
  
  get: async (id: string): Promise<Class> => {
    const response = await api.get(`/classes/${id}`);
    return response.data;
  },
  
  create: async (data: Partial<Class>): Promise<Class> => {
    const response = await api.post('/classes', data);
    return response.data;
  },
  
  update: async (id: string, data: Partial<Class>): Promise<Class> => {
    const response = await api.patch(`/classes/${id}`, data);
    return response.data;
  },
  
  delete: async (id: string): Promise<void> => {
    await api.delete(`/classes/${id}`);
  },
  
  getStudents: async (id: string): Promise<Student[]> => {
    const response = await api.get(`/classes/${id}/students`);
    return response.data;
  },
  
  addStudent: async (id: string, studentId: string): Promise<void> => {
    await api.post(`/classes/${id}/students`, { studentId });
  },
  
  removeStudent: async (id: string, studentId: string): Promise<void> => {
    await api.delete(`/classes/${id}/students/${studentId}`);
  },
  
  getSchedule: async (id: string): Promise<Schedule[]> => {
    const response = await api.get(`/classes/${id}/schedule`);
    return response.data;
  },
  
  getTeachers: async (id: string): Promise<Teacher[]> => {
    const response = await api.get(`/classes/${id}/teachers`);
    return response.data;
  },
  
  addTeacher: async (id: string, teacherId: string, subjectId: string): Promise<void> => {
    await api.post(`/classes/${id}/teachers`, { teacherId, subjectId });
  },
  
  removeTeacher: async (id: string, teacherId: string): Promise<void> => {
    await api.delete(`/classes/${id}/teachers/${teacherId}`);
  },
};

// Subjects endpoints
export const subjectsApi = {
  list: async (params?: PaginationParams & FilterParams): Promise<PaginatedResponse<Subject>> => {
    const queryString = params ? buildQueryString(params) : '';
    const response = await api.get(`/subjects${queryString ? `?${queryString}` : ''}`);
    return response.data;
  },
  
  get: async (id: string): Promise<Subject> => {
    const response = await api.get(`/subjects/${id}`);
    return response.data;
  },
  
  create: async (data: Partial<Subject>): Promise<Subject> => {
    const response = await api.post('/subjects', data);
    return response.data;
  },
  
  update: async (id: string, data: Partial<Subject>): Promise<Subject> => {
    const response = await api.patch(`/subjects/${id}`, data);
    return response.data;
  },

  assignTeacher: async (id: number, data: { teacherId: number | null }) => {
    const response = await api.patch(`/subjects/${id}/assign-teacher`, data);
    return response.data;
  },
  
  delete: async (id: string): Promise<void> => {
    await api.delete(`/subjects/${id}`);
  },
  
  getTeachers: async (id: string): Promise<Teacher[]> => {
    const response = await api.get(`/subjects/${id}/teachers`);
    return response.data;
  },
  
  getClasses: async (id: string): Promise<Class[]> => {
    const response = await api.get(`/subjects/${id}/classes`);
    return response.data;
  },
  
  getAll: async (params?: PaginationParams & FilterParams): Promise<PaginatedResponse<Subject>> => {
    // Alias for list
    return subjectsApi.list(params);
  },
  
  getStats: async (): Promise<ApiResponse<{
    total: number;
    active: number;
    withTeachers: number;
    totalHours: number;
  }>> => {
    const response = await api.get('/subjects/stats');
    return response.data;
  },
};

// Grades endpoints
export const gradesApi = {
  list: async (params?: PaginationParams & FilterParams): Promise<PaginatedResponse<Grade>> => {
    const queryString = params ? buildQueryString(params) : '';
    const response = await api.get(`/grades${queryString ? `?${queryString}` : ''}`);
    return response.data;
  },
  
  getAll: async (): Promise<ApiResponse<Grade[]>> => {
    const response = await api.get('/grades/all');
    return response.data;
  },
  
  get: async (id: string): Promise<Grade> => {
    const response = await api.get(`/grades/${id}`);
    return response.data;
  },
  
  create: async (data: Partial<Grade>): Promise<Grade> => {
    const response = await api.post('/grades', data);
    return response.data;
  },
  
  update: async (id: string, data: Partial<Grade>): Promise<Grade> => {
    const response = await api.patch(`/grades/${id}`, data);
    return response.data;
  },
  
  delete: async (id: string): Promise<void> => {
    await api.delete(`/grades/${id}`);
  },
  
  bulkCreate: async (data: BulkOperation<Partial<Grade>>): Promise<ApiResponse<Grade[]>> => {
    const response = await api.post('/grades/bulk', data);
    return response.data;
  },
  
  createBulk: async (data: { studentId: number; subjectId: number; assessmentType: string; value: number; maxValue: number; period: number; date: string; }[]): Promise<ApiResponse<Grade[]>> => {
    // Map the camelCase properties to snake_case properties expected by the API
    const mappedData = data.map(item => ({
      student_id: item.studentId,
      subject_id: item.subjectId,
      assessment_type: item.assessmentType,
      score: item.value,
      max_score: item.maxValue,
      assessment_date: item.date,
      // Add any other required fields
      period: item.period
    }));
    
    const response = await api.post('/grades/bulk', { items: mappedData });
    return response.data;
  },
  
  bulkUpdate: async (data: BulkOperation<Partial<Grade>>): Promise<ApiResponse<Grade[]>> => {
    const response = await api.patch('/grades/bulk', data);
    return response.data;
  },
  
  export: async (params?: FilterParams): Promise<void> => {
    const queryString = params ? buildQueryString(params) : '';
    await apiHelpers.downloadFile(`/grades/export${queryString ? `?${queryString}` : ''}`, 'grades.xlsx');
  },
  
  import: async (file: File, onProgress?: (progress: number) => void): Promise<ApiResponse<any>> => {
    const response = await apiHelpers.uploadFile('/grades/import', file, onProgress);
    return response.data;
  },
};

// Attendance endpoints
export const attendanceApi = {
  list: async (params?: PaginationParams & FilterParams): Promise<PaginatedResponse<Attendance>> => {
    const queryString = params ? buildQueryString(params) : '';
    const response = await api.get(`/attendance${queryString ? `?${queryString}` : ''}`);
    return response.data;
  },
  
  get: async (id: string): Promise<Attendance> => {
    const response = await api.get(`/attendance/${id}`);
    return response.data;
  },
  
  create: async (data: Partial<Attendance>): Promise<Attendance> => {
    const response = await api.post('/attendance', data);
    return response.data;
  },
  
  update: async (id: string, data: Partial<Attendance>): Promise<Attendance> => {
    const response = await api.patch(`/attendance/${id}`, data);
    return response.data;
  },
  
  delete: async (id: string): Promise<void> => {
    await api.delete(`/attendance/${id}`);
  },
  
  bulkCreate: async (data: BulkOperation<Partial<Attendance>>): Promise<ApiResponse<Attendance[]>> => {
    const response = await api.post('/attendance/bulk', data);
    return response.data;
  },
  
  bulkUpdate: async (data: BulkOperation<Partial<Attendance>>): Promise<ApiResponse<Attendance[]>> => {
    const response = await api.patch('/attendance/bulk', data);
    return response.data;
  },
  
  getByClass: async (classId: string, date: string): Promise<Attendance[]> => {
    const response = await api.get(`/attendance/class/${classId}?date=${date}`);
    return response.data;
  },
  
  getByStudent: async (studentId: string, params?: FilterParams): Promise<PaginatedResponse<Attendance>> => {
    const queryString = params ? buildQueryString(params) : '';
    const response = await api.get(`/attendance/student/${studentId}${queryString ? `?${queryString}` : ''}`);
    return response.data;
  },
  
  export: async (params?: FilterParams): Promise<void> => {
    const queryString = params ? buildQueryString(params) : '';
    await apiHelpers.downloadFile(`/attendance/export${queryString ? `?${queryString}` : ''}`, 'attendance.xlsx');
  },
  createBulk: async (records: Partial<Attendance>[]): Promise<Attendance[]> => {
    const response = await api.post('/attendance/bulk', { records });
    return response.data;
  },
  generateReport: (params: FilterParams) => 
    api.post<Report>('/attendances/generate-report', params),

  getAll: (_value: { 
    classId: string; subjectId: string; dateFrom: string; dateTo: string; status: string;
     }) => api.get<Attendance[]>('/attendances') 

};

// Schedule endpoints
export const scheduleApi = {
  list: async (params?: PaginationParams & FilterParams): Promise<PaginatedResponse<Schedule>> => {
    const queryString = params ? buildQueryString(params) : '';
    const response = await api.get(`/schedule${queryString ? `?${queryString}` : ''}`);
    return response.data;
  },
  
  get: async (id: string): Promise<Schedule> => {
    const response = await api.get(`/schedule/${id}`);
    return response.data;
  },
  
  create: async (data: Partial<Schedule>): Promise<Schedule> => {
    const response = await api.post('/schedule', data);
    return response.data;
  },
  
  update: async (id: string, data: Partial<Schedule>): Promise<Schedule> => {
    const response = await api.patch(`/schedule/${id}`, data);
    return response.data;
  },
  
  delete: async (id: string): Promise<void> => {
    await api.delete(`/schedule/${id}`);
  },
  
  getByClass: async (classId: string): Promise<Schedule[]> => {
    const response = await api.get(`/schedule/class/${classId}`);
    return response.data;
  },
  
  getByTeacher: async (teacherId: string): Promise<Schedule[]> => {
    const response = await api.get(`/schedule/teacher/${teacherId}`);
    return response.data;
  },
  
  getByStudent: async (studentId: string): Promise<Schedule[]> => {
    const response = await api.get(`/schedule/student/${studentId}`);
    return response.data;
  },
  
  bulkCreate: async (data: BulkOperation<Partial<Schedule>>): Promise<ApiResponse<Schedule[]>> => {
    const response = await api.post('/schedule/bulk', data);
    return response.data;
  },
  
  export: async (filters?: any): Promise<{ data: Blob }> => {
    const queryString = filters ? buildQueryString(filters) : '';
    const response = await api.get(`/schedule/export${queryString ? `?${queryString}` : ''}`, {
      responseType: 'blob'
    });
    return { data: response.data };
  },
};

// Events endpoints
export const eventsApi = {
  list: async (params?: PaginationParams & FilterParams): Promise<PaginatedResponse<Event>> => {
    const queryString = params ? buildQueryString(params) : '';
    const response = await api.get(`/events${queryString ? `?${queryString}` : ''}`);
    return response.data;
  },
  
  get: async (id: string): Promise<Event> => {
    const response = await api.get(`/events/${id}`);
    return response.data;
  },
  
  create: async (data: Partial<Event>): Promise<Event> => {
    const response = await api.post('/events', data);
    return response.data;
  },
  
  update: async (id: string, data: Partial<Event>): Promise<Event> => {
    const response = await api.patch(`/events/${id}`, data);
    return response.data;
  },
  
  delete: async (id: string): Promise<void> => {
    await api.delete(`/events/${id}`);
  },
  
  getUpcoming: async (limit?: number): Promise<Event[]> => {
    const queryString = limit ? buildQueryString({ limit }) : '';
    const response = await api.get(`/events/upcoming${queryString ? `?${queryString}` : ''}`);
    return response.data;
  },
  
  getByDate: async (date: string): Promise<Event[]> => {
    const response = await api.get(`/events/date/${date}`);
    return response.data;
  },
  
  getByDateRange: async (startDate: string, endDate: string): Promise<Event[]> => {
    const response = await api.get(`/events/range?start=${startDate}&end=${endDate}`);
    return response.data;
  },
};

// Payments endpoints
export const paymentsApi = {
  list: async (params?: PaginationParams & FilterParams): Promise<PaginatedResponse<Payment>> => {
    const queryString = params ? buildQueryString(params) : '';
    const response = await api.get(`/payments${queryString ? `?${queryString}` : ''}`);
    return response.data;
  },
  
  get: async (id: string): Promise<Payment> => {
    const response = await api.get(`/payments/${id}`);
    return response.data;
  },
  
  create: async (data: Partial<Payment>): Promise<Payment> => {
    const response = await api.post('/payments', data);
    return response.data;
  },
  
  update: async (id: string, data: Partial<Payment>): Promise<Payment> => {
    const response = await api.patch(`/payments/${id}`, data);
    return response.data;
  },
  
  delete: async (id: string): Promise<void> => {
    await api.delete(`/payments/${id}`);
  },
  
  process: async (id: string, data: any): Promise<Payment> => {
    const response = await api.post(`/payments/${id}/process`, data);
    return response.data;
  },
  
  getByStudent: async (studentId: string, params?: FilterParams): Promise<PaginatedResponse<Payment>> => {
    const queryString = params ? buildQueryString(params) : '';
    const response = await api.get(`/payments/student/${studentId}${queryString ? `?${queryString}` : ''}`);
    return response.data;
  },
  
  generateInvoice: async (id: string): Promise<void> => {
    await apiHelpers.downloadFile(`/payments/${id}/invoice`, `invoice-${id}.pdf`);
  },
  
  export: async (params?: FilterParams): Promise<void> => {
    const queryString = params ? buildQueryString(params) : '';
    await apiHelpers.downloadFile(`/payments/export${queryString ? `?${queryString}` : ''}`, 'payments.xlsx');
  },
};

// Dashboard endpoints
export const dashboardApi = {
  getStats: async (): Promise<DashboardStats> => {
    const response = await api.get('/dashboard/stats');
    return response.data;
  },
  
  getAdminStats: async (): Promise<DashboardStats> => {
    const response = await api.get('/dashboard/admin/stats');
    return response.data;
  },

  getAll: async (params?: PaginationParams & FilterParams): Promise<PaginatedResponse<Schedule>> => {
    const queryString = params ? buildQueryString(params) : '';
    const response = await api.get(`/schedule${queryString ? `?${queryString}` : ''}`);
    return response.data;
  },
  
  getTeacherStats: async (): Promise<DashboardStats> => {
    const response = await api.get('/dashboard/teacher/stats');
    return response.data;
  },
  
  getStudentStats: async (): Promise<DashboardStats> => {
    const response = await api.get('/dashboard/student/stats');
    return response.data;
  },
  
  getParentStats: async (): Promise<DashboardStats> => {
    const response = await api.get('/dashboard/parent/stats');
    return response.data;
  },
  
  getRecentActivity: async (limit?: number): Promise<any[]> => {
    const queryString = limit ? buildQueryString({ limit }) : '';
    const response = await api.get(`/dashboard/activity${queryString ? `?${queryString}` : ''}`);
    return response.data;
  },
};

// Reports endpoints
export const reportsApi = {
  list: async (params?: PaginationParams & FilterParams): Promise<PaginatedResponse<Report>> => {
    const queryString = params ? buildQueryString(params) : '';
    const response = await api.get(`/reports${queryString ? `?${queryString}` : ''}`);
    return response.data;
  },
  
  get: async (id: string): Promise<Report> => {
    const response = await api.get(`/reports/${id}`);
    return response.data;
  },
  
  generate: async (type: string, params?: any): Promise<Report> => {
    const response = await api.post('/reports/generate', { type, params });
    return response.data;
  },
  
  download: async (id: string, format: string): Promise<void> => {
    await apiHelpers.downloadFile(`/reports/${id}/download?format=${format}`, `report.${format}`);
  },
  
  getTemplates: async (): Promise<any[]> => {
    const response = await api.get('/reports/templates');
    return response.data;
  },
  
  delete: async (id: string): Promise<void> => {
    await api.delete(`/reports/${id}`);
  },
  
  export: async (_params: any): Promise<void> => {
    await apiHelpers.downloadFile('/reports/export', 'report.pdf');
  },
};

// Notifications endpoints
export const notificationsApi = {
  list: async (params?: PaginationParams & FilterParams): Promise<PaginatedResponse<Notification>> => {
    const queryString = params ? buildQueryString(params) : '';
    const response = await api.get(`/notifications${queryString ? `?${queryString}` : ''}`);
    return response.data;
  },
  
  get: async (id: string): Promise<Notification> => {
    const response = await api.get(`/notifications/${id}`);
    return response.data;
  },
  
  markAsRead: async (id: string): Promise<void> => {
    await api.patch(`/notifications/${id}/read`);
  },
  
  markAllAsRead: async (): Promise<void> => {
    await api.patch('/notifications/read-all');
  },
  
  delete: async (id: string): Promise<void> => {
    await api.delete(`/notifications/${id}`);
  },
  
  getUnreadCount: async (): Promise<number> => {
    const response = await api.get('/notifications/unread-count');
    return response.data.count;
  },
  
  send: async (data: Partial<Notification>): Promise<Notification> => {
    const response = await api.post('/notifications', data);
    return response.data;
  },
  
  deleteAll: async (): Promise<void> => {
    await api.delete('/notifications');
  },
  
  bulkSend: async (data: BulkOperation<Partial<Notification>>): Promise<ApiResponse<Notification[]>> => {
    const response = await api.post('/notifications/bulk', data);
    return response.data;
  },
};

// Settings endpoints
export const settingsApi = {
  get: async (): Promise<SchoolSettings> => {
    const response = await api.get('/settings');
    return response.data;
  },
  
  update: async (data: Partial<SchoolSettings>): Promise<SchoolSettings> => {
    const response = await api.patch('/settings', data);
    return response.data;
  },
  
  getLogo: async (): Promise<string> => {
    const response = await api.get('/settings/logo');
    return response.data.url;
  },
  
  uploadLogo: async (file: File, onProgress?: (progress: number) => void): Promise<FileUpload> => {
    const response = await apiHelpers.uploadFile('/settings/logo', file, onProgress);
    return response.data;
  },
  
  getTheme: async (): Promise<any> => {
    const response = await api.get('/settings/theme');
    return response.data;
  },
  
  updateTheme: async (data: any): Promise<any> => {
    const response = await api.patch('/settings/theme', data);
    return response.data;
  },
};

// File upload endpoints
export const filesApi = {
  upload: async (file: File, onProgress?: (progress: number) => void): Promise<FileUpload> => {
    const response = await apiHelpers.uploadFile('/files/upload', file, onProgress);
    return response.data;
  },
  
  uploadAvatar: async (file: File, onProgress?: (progress: number) => void): Promise<FileUpload> => {
    const response = await apiHelpers.uploadFile('/files/upload/avatar', file, onProgress);
    return response.data;
  },
  
  uploadDocument: async (file: File, onProgress?: (progress: number) => void): Promise<FileUpload> => {
    const response = await apiHelpers.uploadFile('/files/upload/document', file, onProgress);
    return response.data;
  },
  
  uploadBulk: async (files: File[], onProgress?: (progress: number) => void): Promise<FileUpload[]> => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    
    const response = await api.post('/files/upload/bulk', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });
    
    return response.data;
  },
  
  delete: async (id: string): Promise<void> => {
    await api.delete(`/files/${id}`);
  },
  
  get: async (id: string): Promise<FileUpload> => {
    const response = await api.get(`/files/${id}`);
    return response.data;
  },
  
  download: async (id: string, filename?: string): Promise<void> => {
    await apiHelpers.downloadFile(`/files/${id}/download`, filename);
  },
};

// Messages API
export const messagesApi = {
  // Get all conversations
  getConversations: (params?: PaginationParams & FilterParams): Promise<PaginatedResponse<any>> =>
    api.get('/messages/conversations', { params }),

  // Get conversation messages
  getMessages: (conversationId: number, params?: PaginationParams): Promise<PaginatedResponse<any>> =>
    api.get(`/messages/conversations/${conversationId}/messages`, { params }),

  // Send message
  sendMessage: (conversationId: number, data: { content: string; attachments?: FileUpload[] }): Promise<ApiResponse<any>> =>
    api.post(`/messages/conversations/${conversationId}/messages`, data),

  // Create conversation
  createConversation: (data: { name: string; type: 'individual' | 'group' | 'announcement'; participants: number[] }): Promise<ApiResponse<any>> =>
    api.post('/messages/conversations', data),

  // Mark as read
  markAsRead: (conversationId: number): Promise<ApiResponse<void>> =>
    api.patch(`/messages/conversations/${conversationId}/read`),

  // Delete message
  deleteMessage: (messageId: number): Promise<ApiResponse<void>> =>
    api.delete(`/messages/${messageId}`),

  // Search messages
  searchMessages: (query: string, params?: FilterParams): Promise<PaginatedResponse<any>> =>
    api.get('/messages/search', { params: { q: query, ...params } }),
};

// Enrollments endpoints
export const enrollmentsApi = {
  list: async (params?: PaginationParams & FilterParams): Promise<PaginatedResponse<any>> => {
    const queryString = params ? buildQueryString(params) : '';
    const response = await api.get(`/enrollments${queryString ? `?${queryString}` : ''}`);
    return response.data;
  },
  get: async (id: string): Promise<any> => {
    const response = await api.get(`/enrollments/${id}`);
    return response.data;
  },
  create: async (data: any): Promise<any> => {
    const response = await api.post('/enrollments', data);
    return response.data;
  },
  update: async (id: string, data: any): Promise<any> => {
    const response = await api.patch(`/enrollments/${id}`, data);
    return response.data;
  },
  approve: async (id: number): Promise<void> => {
    await api.post(`/enrollments/${id}/approve`);
  },
  cancel: async (id: number): Promise<void> => {
    await api.post(`/enrollments/${id}/cancel`);
  },
  export: async (params?: FilterParams): Promise<void> => {
    const queryString = params ? buildQueryString(params) : '';
    await apiHelpers.downloadFile(`/enrollments/export${queryString ? `?${queryString}` : ''}`, 'enrollments.xlsx');
  },
  getStats: async (): Promise<any> => {
    const response = await api.get('/enrollments/stats');
    return response.data;
  },
};

// Announcements endpoints
export const announcementsApi = {
  getAll: async (params?: PaginationParams & FilterParams): Promise<PaginatedResponse<any>> => {
    const queryString = params ? buildQueryString(params) : '';
    const response = await api.get(`/announcements${queryString ? `?${queryString}` : ''}`);
    return response.data;
  },
  get: async (id: number | string): Promise<any> => {
    const response = await api.get(`/announcements/${id}`);
    return response.data;
  },
  create: async (data: any): Promise<any> => {
    const response = await api.post('/announcements', data);
    return response.data;
  },
  update: async (id: number | string, data: any): Promise<any> => {
    const response = await api.patch(`/announcements/${id}`, data);
    return response.data;
  },
  delete: async (id: number | string): Promise<void> => {
    await api.delete(`/announcements/${id}`);
  },
  publish: async (id: number | string): Promise<void> => {
    await api.post(`/announcements/${id}/publish`);
  },
  archive: async (id: number | string): Promise<void> => {
    await api.post(`/announcements/${id}/archive`);
  },
  export: async (params?: FilterParams): Promise<void> => {
    const queryString = params ? buildQueryString(params) : '';
    await apiHelpers.downloadFile(`/announcements/export${queryString ? `?${queryString}` : ''}`, `announcements-${new Date().toISOString().split('T')[0]}.xlsx`);
  },
};

// Expenses endpoints
export const expensesApi = {
  getAll: async (params?: PaginationParams & FilterParams): Promise<PaginatedResponse<any>> => {
    const queryString = params ? buildQueryString(params) : '';
    const response = await api.get(`/expenses${queryString ? `?${queryString}` : ''}`);
    return response.data;
  },
  get: async (id: number | string): Promise<any> => {
    const response = await api.get(`/expenses/${id}`);
    return response.data;
  },
  create: async (data: any): Promise<any> => {
    const response = await api.post('/expenses', data);
    return response.data;
  },
  update: async (id: number | string, data: any): Promise<any> => {
    const response = await api.patch(`/expenses/${id}`, data);
    return response.data;
  },
  delete: async (id: number | string): Promise<void> => {
    await api.delete(`/expenses/${id}`);
  },
  approve: async (id: number | string): Promise<void> => {
    await api.post(`/expenses/${id}/approve`);
  },
  reject: async (id: number | string, data: { reason: string }): Promise<void> => {
    await api.post(`/expenses/${id}/reject`, data);
  },
  bulkApprove: async (ids: (number | string)[]): Promise<void> => {
    await api.post('/expenses/bulk-approve', { ids });
  },
  export: async (params?: FilterParams): Promise<{ data: Blob }> => {
    const queryString = params ? buildQueryString(params) : '';
    const response = await api.get(`/expenses/export${queryString ? `?${queryString}` : ''}`, {
      responseType: 'blob'
    });
    return { data: response.data };
  },
};

// Export all APIs
export default {
  auth: authApi,
  users: usersApi,
  students: studentsApi,
  teachers: teachersApi,
  parents: parentsApi,
  classes: classesApi,
  subjects: subjectsApi,
  grades: gradesApi,
  attendance: attendanceApi,
  schedule: scheduleApi,
  events: eventsApi,
  payments: paymentsApi,
  dashboard: dashboardApi,
  reports: reportsApi,
  notifications: notificationsApi,
  settings: settingsApi,
  files: filesApi,
  messages: messagesApi, 
  enrollments: enrollmentsApi,
  announcements: announcementsApi,
  expenses: expensesApi,
};